import React, { useEffect, useRef } from 'react'
import { Button, Spin } from 'antd'
import type { ReactNode } from 'react'
import type { ErrorKind } from '../error-kind'
import { isRetryable } from '../error-kind'
import type { BootTier } from './boot-phase'

/*
 * These screens render when identity or session may be what failed, so they use no IoC services,
 * AppState, aqe context or user context: only antd and inline styles. Colors come from theme
 * variables with a fallback, since the theme may not have loaded either.
 */

const CENTERED: React.CSSProperties = { textAlign: 'center', paddingTop: '20vh' }
const TITLE: React.CSSProperties = { fontSize: 24, fontWeight: 'bold', margin: 0, outline: 'none' }
const DETAIL: React.CSSProperties = { fontSize: 16, margin: '8px 0 16px' }
const REFERENCE: React.CSSProperties = { fontSize: 14, margin: '16px 0 0', opacity: 0.8 }

/**
 * A full-page failure replaces what the user was doing, so it interrupts (role alert) and takes
 * focus on its heading, so a screen reader reads the title and the keyboard starts at the top.
 */
const FailureScreen = ({ testId, title, children }: { testId: string; title: string; children: ReactNode }) => {
    const heading = useRef<HTMLHeadingElement>(null)

    useEffect(() => {
        heading.current?.focus()
    }, [])

    return (
        <div role="alert" data-testid={testId} style={CENTERED}>
            <h1 ref={heading} tabIndex={-1} style={TITLE}>
                {title}
            </h1>
            {children}
        </div>
    )
}

export type BootRestoringProps = {
    /** True while an expired session is being signed back in. The region stays mounted either way. */
    active: boolean
}

/**
 * Shown over the mounted app while an expired session is restored. It never blocks the page. The
 * live region is in the DOM before the restore starts and is filled when it does, which is what
 * makes the text get announced.
 */
export const BootRestoring = ({ active }: BootRestoringProps) => (
    <div role="status" aria-live="polite" data-testid="boot-status">
        {active ? (
            <div
                data-testid="boot-restoring"
                style={{
                    position: 'fixed',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 2000,
                    padding: '8px 16px',
                    borderRadius: 4,
                    background: 'var(--aqe-color-bg, #fff)',
                    color: 'var(--aqe-color-text, inherit)',
                    boxShadow: 'var(--shadow-sm, 0 2px 8px rgba(0, 0, 0, 0.2))',
                    pointerEvents: 'none',
                }}
            >
                <Spin size="small" /> <span style={{ marginLeft: 8 }}>Signing you back in</span>
            </div>
        ) : null}
    </div>
)

type FailedAction = 'retry' | 'reload'

type KindCopy = {
    title: string
    /** Receives the plain-words description of the tier that failed. */
    detail: (where: string) => string
    action: FailedAction
}

const WHERE: Record<BootTier, string> = {
    identity: 'while loading the app settings',
    session: 'while loading your account',
    access: 'while loading your access',
}

const CONTACT_SUPPORT = 'If it keeps happening, contact support and quote the reference below.'

/** Only a transient failure can be retried; everything else reloads, since a retry runs the same request. */
const COPY: Record<ErrorKind, KindCopy> = {
    transient: {
        title: 'We could not reach the service',
        detail: (where) => `This happened ${where}. Check your connection and try again.`,
        action: 'retry',
    },
    misconfigured: {
        title: 'Something is wrong on our side',
        detail: (where) => `This happened ${where}. Reloading may pick up a fix. ${CONTACT_SUPPORT}`,
        action: 'reload',
    },
    invalid: {
        title: 'The service rejected the request',
        detail: (where) => `This happened ${where}. Reloading may pick up a fix. ${CONTACT_SUPPORT}`,
        action: 'reload',
    },
    forbidden: {
        title: 'You do not have access',
        detail: (where) =>
            `The service refused the request ${where}. If you think this is a mistake, ask an administrator.`,
        action: 'reload',
    },
    not_found: {
        title: 'Not found, or you may not have access',
        detail: (where) => `This happened ${where}. If you think this is a mistake, ask an administrator.`,
        action: 'reload',
    },
    unauthenticated: {
        title: 'Your session ended',
        detail: (where) => `It ended ${where}. Reload the page to sign in again.`,
        action: 'reload',
    },
    // A canceled load never fails boot, so this copy is never shown; the record must still be complete.
    aborted: {
        title: 'Loading was interrupted',
        detail: (where) => `This happened ${where}. Reload the page to start again.`,
        action: 'reload',
    },
}

/**
 * The identity provider refused the sign-in. The provider's description follows as the reason. Retry
 * runs the session check, which with no session goes to sign-in.
 */
const SIGN_IN_REFUSED: KindCopy = {
    title: 'Your sign-in was refused',
    detail: () => 'The sign-in provider did not let you in. If you think this is a mistake, ask an administrator.',
    action: 'retry',
}

const ACTION_LABEL: Record<FailedAction, string> = { retry: 'Retry', reload: 'Reload' }

const reloadPage = () => window.location.reload()

export type BootFailedProps = {
    tier: BootTier
    kind: ErrorKind
    /** The id of the error report, shown so a support conversation can find the report. */
    referenceId?: string
    /** The reason a person outside this app gave, such as the identity provider's description. Shown as text. */
    reason?: string
    onRetry: () => void
}

/** The raw error is never shown; it is reported by whoever sent the failure event. */
export const BootFailed = ({ tier, kind, referenceId, reason, onRetry }: BootFailedProps) => {
    const refused = kind === 'forbidden' && tier === 'session'
    const copy = refused ? SIGN_IN_REFUSED : COPY[kind]
    return (
        <FailureScreen testId="boot-failed" title={copy.title}>
            <div style={DETAIL}>{copy.detail(WHERE[tier])}</div>
            {reason ? (
                <div data-testid="boot-failed-reason" style={DETAIL}>
                    {reason}
                </div>
            ) : null}
            <Button type="primary" size="large" onClick={copy.action === 'retry' ? onRetry : reloadPage}>
                {ACTION_LABEL[copy.action]}
            </Button>
            {referenceId && !isRetryable(kind) && !refused ? (
                <div data-testid="boot-failed-reference" style={REFERENCE}>
                    Reference: <code>{referenceId}</code>
                </div>
            ) : null}
        </FailureScreen>
    )
}

/**
 * The user's account has no domains, so there is nothing to retry and no screen to show. Only an
 * administrator can change that; Reload picks up the change.
 */
export const BootNoDomains = () => (
    <FailureScreen testId="boot-no-domains" title="Your account has no domains yet">
        <div style={DETAIL}>
            An administrator needs to give you access to a domain. Once they have, reload this page.
        </div>
        <Button type="primary" size="large" onClick={reloadPage}>
            Reload
        </Button>
    </FailureScreen>
)
